import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db, storage } from '../config/fbConfig';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, Timestamp, query, where, or } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Modal from 'react-modal';
import _ from 'lodash';
import Logo from '../images/school_logo.png';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import '../styles/Students.css';
import { SCHOOL_ACRONYM } from "../Variables";
import * as XLSX from 'xlsx'; // For Excel file parsing
import Papa from 'papaparse'; // For CSV file parsing

Modal.setAppElement('#root');

const Students = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [update, setUpdate] = useState(false);
  const [students, setStudents] = useState([]);
  const [newStudent, setNewStudent] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    bloodGroup: '',
    religion: '',
    homeAddress: '',
    city: '',
    nationality: '',
    NIN: '',
    fathersName: '',
    fathersAddress: '',
    fathersPlaceOfWork: '',
    fathersEmail: '',
    fathersPhone1: '',
    fathersPhone2: '',
    studentPicture: null,
    status: 'Regular',
    discountAmount: '',
  });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [isFetchingStudents, setIsFetchingStudents] = useState(true);
  const [searchStudent, setSearchStudent] = useState("");
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel'); // 'excel' or 'csv'
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');

  const studentsCollectionRef = collection(db, "students");

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async (searchTerm = "") => {
    setIsFetchingStudents(true);
    try {
      const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  
      if (normalizedSearchTerm === "all") {
        // Fetch ALL students
        const data = await getDocs(studentsCollectionRef);
        setStudents(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      } 
      else if (searchTerm.trim()) {
        // Fetch all students and filter locally (for case-insensitive search)
        const allStudentsSnapshot = await getDocs(studentsCollectionRef);
        const allStudents = allStudentsSnapshot.docs.map(doc => ({ 
          ...doc.data(), 
          id: doc.id 
        }));
  
        const filteredStudents = allStudents.filter(student => {
          const fullName = `${student.firstName || ''} ${student.lastName || ''}`.toLowerCase();
          const admissionNumber = (student.admissionNumber || '').toLowerCase();
          
          return (
            fullName.includes(normalizedSearchTerm) ||
            admissionNumber.includes(normalizedSearchTerm)
          );
        });
  
        setStudents(filteredStudents);
      } 
      else {
        setStudents([]); // Clear results if search is empty
      }
    } catch (error) {
      console.error("Firestore error:", error);
      setError("Failed to fetch students. Please try again.");
      setShowModal(true);
    } finally {
      setIsFetchingStudents(false);
    }
  };  

  const handleInputChange = (e) => {
    const { name, value, files, type } = e.target;
    
    if (files) {
      setNewStudent({ ...newStudent, [name]: files[0] });
    } else if (type === 'number') {
      setNewStudent({ ...newStudent, [name]: value === '' ? '' : Number(value) });
    } else {
      setNewStudent({ ...newStudent, [name]: value });
    }
  };
  
  const generateAdmissionNumber = async () => {
    const now = new Date();
    const second = now.getSeconds().toString().padStart(2, '0'); // 00-59
    const millisecond = now.getMilliseconds().toString().padStart(3, '0'); // 000-999
    let counter = 0; // Counter for uniqueness within the same millisecond
  
    while (true) {
      // Generate a 7-digit number: second (2 digits) + millisecond (3 digits) + counter (2 digits)
      const counterStr = counter.toString().padStart(2, '0');
      const numberPart = `${second}${millisecond}${counterStr}`.slice(0, 4); // Ensure exactly 4 digits
      const admissionNumber = `${SCHOOL_ACRONYM}/${numberPart}`; // e.g., USIC/5112345
  
      // Check for duplicates in Firestore
      const studentsRef = collection(db, 'students');
      const q = query(studentsRef, where('admissionNumber', '==', admissionNumber));
      const querySnapshot = await getDocs(q);
  
      if (querySnapshot.empty) {
        return admissionNumber; // Unique number found
      }
  
      counter++;
      if (counter > 99) {
        // Wait 1ms and retry with new timestamp
        await new Promise(resolve => setTimeout(resolve, 1));
        return generateAdmissionNumber();
      }
    }
  };

  const handleAddStudent = async () => {
    setError("");
    setSuccessMessage("") ;

    const requiredFields = {
      firstName: "First name",
      lastName: "Last name",
      gender: "Gender",
      religion: "Religion",
      city: "City",
      nationality: "Nationality",
      // admissionNumber: "Admission Number",
    };
    for (const [field, displayName] of Object.entries(requiredFields)) {
      const value = field === "name" ? newStudent[field].trim() : newStudent[field];
      if (!value) {
        setError(`${displayName} cannot be empty`);
        setShowModal(true);
        return;
      }
    }  
    setUpdate(true);
    setError("");
    setSuccessMessage("") ;
    try {
      const admissionNumber = await generateAdmissionNumber();
      const studentData = { ...newStudent, admissionNumber, enrollments: [], createdAt: Timestamp.now() };

      // if (studentData.studentPicture) {
      //   const pictureRef = ref(storage, `students/${admissionNumber}`);
      //   await uploadBytes(pictureRef, studentData.studentPicture);
      //   studentData.studentPicture = await getDownloadURL(pictureRef);
      // }

      await addDoc(studentsCollectionRef, studentData);

      setSuccessMessage("Student added successfully");
      fetchStudents();
      setNewStudent({
        firstName: '',
        middleName: '',
        lastName: '',
        admissionNumber: '',
        gender: '',
        dateOfBirth: '',
        bloodGroup: '',
        religion: '',
        homeAddress: '',
        city: '',
        nationality: '',
        NIN: '',
        fathersName: '',
        fathersAddress: '',
        fathersPlaceOfWork: '',
        fathersEmail: '',
        fathersPhone1: '',
        fathersPhone2: '',
        studentPicture: null,
      });
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add student");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleEditStudent = async () => {
    setUpdate(true);
    try {
      const studentData = { ...newStudent, updatedAt: Timestamp.now() };

      if (studentData.studentPicture && typeof studentData.studentPicture !== 'string') {
        const pictureRef = ref(storage, `students/${newStudent.admissionNumber}`);
        await uploadBytes(pictureRef, studentData.studentPicture);
        studentData.studentPicture = await getDownloadURL(pictureRef);
      }

      const studentDoc = doc(db, "students", currentStudent.id);
      await updateDoc(studentDoc, studentData);
      setSuccessMessage("Student updated successfully");
      fetchStudents();
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update student");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleDeleteStudent = async (id) => {
    setUpdate(true);
    try {
      const studentDoc = doc(db, "students", id);
      await deleteDoc(studentDoc);
      setIsDeleteModalOpen(false);
      setSuccessMessage("Student deleted successfully");
      fetchStudents();
    } catch (error) {
      setError("Failed to delete student");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };
  
  const handleFileTypeChange = (e) => {
    setFileType(e.target.value);
  };
  
  const processBulkImport = async () => {
    if (!file) {
      setError("Please select a file first");
      setShowModal(true);
      return;
    }

    setImportStatus('Processing file...');
    setImportProgress(0);
  
    try {
      let studentsData = [];
  
      if (fileType === 'excel') {
        // Process Excel file
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        studentsData = XLSX.utils.sheet_to_json(worksheet);
      } else {
        // Process CSV file
        const text = await file.text();
        const result = Papa.parse(text, {
          header: true,
          skipEmptyLines: true
        });
        studentsData = result.data;
      }
  
      if (studentsData.length === 0) {
        throw new Error("No student data found in the file");
      }
  
      setImportStatus(`Importing ${studentsData.length} students...`);
      
      let successCount = 0;
      let errorCount = 0;
      
      for (let i = 0; i < studentsData.length; i++) {
        try {
          const student = studentsData[i];
          
          // Validate required fields
          if (!student.firstName || !student.lastName || !student.gender) {
            errorCount++;
            continue;
          }
  
          const admissionNumber = await generateAdmissionNumber();
          const studentData = {
            firstName: student.firstName || '',
            middleName: student.middleName || '',
            lastName: student.lastName || '',
            admissionNumber: admissionNumber,
            gender: student.gender || '',
            dateOfBirth: student.dateOfBirth || '',
            bloodGroup: student.bloodGroup || '',
            religion: student.religion || '',
            homeAddress: student.homeAddress || '',
            city: student.city || '',
            nationality: student.nationality || 'Nigerian',
            NIN: student.NIN || '',
            fathersName: student.fathersName || '',
            fathersAddress: student.fathersAddress || '',
            fathersPlaceOfWork: student.fathersPlaceOfWork || '',
            fathersEmail: student.fathersEmail || '',
            fathersPhone1: student.fathersPhone1 || '',
            fathersPhone2: student.fathersPhone2 || '',
            enrollments: [],
            createdAt: Timestamp.now()
          };
  
          await addDoc(studentsCollectionRef, studentData);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Error importing student ${i + 1}:`, error);
        }
  
        // Update progress
        setImportProgress(Math.round(((i + 1) / studentsData.length) * 100));
      }
  
      setImportStatus(`Import complete: ${successCount} successful, ${errorCount} failed`);
      setSuccessMessage(`Bulk import completed: ${successCount} students added successfully`);
      fetchStudents();
    } catch (error) {
      setError(`Bulk import failed: ${error.message}`);
      setShowModal(true);
    } finally {
      setUpdate(false);
      setFile(null);
      setTimeout(() => {
        setIsBulkImportModalOpen(false);
        setImportProgress(0);
        setImportStatus('');
      }, 2000);
    }
  };

  const openEditModal = (student) => {
    setNewStudent(student);
    setCurrentStudent(student);
    setIsEditModalOpen(true);
  };

  const openViewModal = (student) => {
    setCurrentStudent(student);
    setIsViewModalOpen(true);
  };

  const openDeleteModal = (student) => {
    setCurrentStudent(student);
    setIsDeleteModalOpen(true);
  };

  if (loading) {
    return <Loader text={"LOADING"} />;
  }

  if (!userData) {
    return <Loader text={"READING"} />;
  }

  const allowedRoles = ['Admin', 'HR', 'Cashier', 'Supreme'];

  return (
    <>
      {update && <Loader text={"PROCESSING"} />}
      {allowedRoles.includes(userData.status) ? (
        <>
      <div className="top-elements row align-center space-between">
        <Link to="/home" className="back-link">
          <i className="fa-solid fa-arrow-left size3"></i>
        </Link>
        <div className="column align-center justify-center">
          <img src={Logo} className="middle-logo" alt="logo" />
        </div>
        <Link to="#" className="home-link" onClick={() => fetchStudents()}>
          <i className="fa-solid fa-rotate-right size4"></i>
        </Link>
      </div>
      <div className="login-page-items column align-center scroll-fixed">
        <h2 className="manage-page-heading caps size4">Manage Students</h2>        
        <div className="row justify-center align-center gap-15 add-buttons">
          <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>
            Add Student
          </button>
          <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>
            Bulk Import
          </button>
        </div>
        <div className="search-container">
          <form 
            className="row space-between"
            onSubmit={(e) => {
              e.preventDefault(); // Prevent page reload
              fetchStudents(searchStudent); // Trigger search
            }}
          >
            <input
              type="text"
              placeholder="Search by name or admission number ('All' for all students)"
              value={searchStudent}
              onChange={(e) => setSearchStudent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  fetchStudents(searchStudent);
                }
              }}
            />
            <button type="submit" className="login-button login-button-search">
              <i className="fas fa-search"></i>
            </button>
          </form>
        </div>

        <div className="scroll-fixed scroll-fixed2">
          <div className="class-list class-list-d scrollable">
          {isFetchingStudents ? (
              <p className="male-female-stat">Loading students...</p>
            ) : students.length === 0 ? (
              <p className="male-female-stat">Search to view students of enter 'All' to view all students</p>
            ) : (
              students.map((student) => (
              <div key={student.id} className="class-item student-item row">
                <div className="student-item-details column">
                  <p>{student.firstName} {student.lastName} {student.middleName}</p><br />
                  <span className={`bold ${student.status?.toLowerCase() || 'regular'}`} style={{marginBottom: '-20px'}}>
                    {student.status || 'Regular'}
                    {student.status === 'Discount' && student.discountAmount && 
                      ` - ₦${Number(student.discountAmount).toLocaleString()}`}
                  </span>
                  <div className="row align-center justify-center gap-15">
                    <button onClick={() => openEditModal(student)} className="login-button caps"><i className="fas fa-edit"></i></button>
                    <button onClick={() => openDeleteModal(student)} className="login-button caps"><i className="fas fa-trash-alt"></i></button>
                    <button onClick={() => openViewModal(student)} className="login-button caps"><i className="fas fa-eye"></i></button>
                  </div>
                </div>
                <div className="student-image-div" style={{backgroundImage: `url(${student.studentPicture})`}}></div>
              </div>
            ))
          )}
          </div>
        </div>
      </div>
      </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Student Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Student"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Student</h2>
        <form className="student-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>          
          <input
            type="text"
            name="firstName"
            className="input-field"
            placeholder="First Name"
            value={newStudent.firstName}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="middleName"
            className="input-field"
            placeholder="Middle Name"
            value={newStudent.middleName}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="lastName"
            className="input-field"
            placeholder="Last Name"
            value={newStudent.lastName}
            onChange={handleInputChange}
          />
          <select
            name="status"
            className="input-field"
            value={newStudent.status || 'Regular'}
            onChange={handleInputChange}
          >
            <option value="Regular">Regular</option>
            <option value="Discount">Discount</option>
            <option value="Scholarship">Scholarship</option>
          </select>

          {/* Conditional discount amount field */}
          {newStudent.status === 'Discount' && (
            <div className="conditional-field">
              <label className='size5 bold'>Discount Amount (₦)</label>
              <input
                type="number"
                name="discountAmount"
                className="input-field"
                placeholder="Enter discount amount"
                value={newStudent.discountAmount || ''}
                onChange={handleInputChange}
                min="0"
                step="100"
              />
            </div>
          )}
          <select
            name="gender"
            className="input-field"
            value={newStudent.gender}
            onChange={handleInputChange}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
          <input
            type="date"
            name="dateOfBirth"
            className="input-field"
            placeholder="D.O.B"
            value={newStudent.dateOfBirth}
            onChange={handleInputChange}
          />
          <select
            name="bloodGroup"
            className="input-field"
            value={newStudent.bloodGroup}
            onChange={handleInputChange}
          >
            <option value="">Select Blood Group</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="O">O</option>
            <option value="AB">AB</option>
          </select>
          <select
            name="religion"
            className="input-field"
            value={newStudent.religion}
            onChange={handleInputChange}
          >
            <option value="">Select Religion</option>
            <option value="Islam">Islam</option>
            <option value="Christianity">Christianity</option>
            <option value="Not Specified">Not Specified</option>
          </select>
          <input
            type="text"
            name="homeAddress"
            className="input-field"
            placeholder="Home Address"
            value={newStudent.homeAddress}
            onChange={handleInputChange}
          />
          <select
            name="city"
            className="input-field"
            value={newStudent.city}
            onChange={handleInputChange}
          >
            <option value="">Select City</option>
            <option value="Kano">Kano</option>
          </select>
          <select
            name="nationality"
            className="input-field"
            value={newStudent.nationality}
            onChange={handleInputChange}
          >
            <option value="">Select Nationality</option>
            <option value="Nigerian">Nigerian</option>
            <option value="Others">Others</option>
          </select>
          <input
            type="text"
            name="NIN"
            className="input-field"
            placeholder="NIN"
            value={newStudent.NIN}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="fathersName"
            className="input-field"
            placeholder="Father's Name"
            value={newStudent.fathersName}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="fathersAddress"
            className="input-field"
            placeholder="Father's Address"
            value={newStudent.fathersAddress}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="fathersPlaceOfWork"
            className="input-field"
            placeholder="Place of Work"
            value={newStudent.fathersPlaceOfWork}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="fathersEmail"
            className="input-field"
            placeholder="Father's Email"
            value={newStudent.fathersEmail}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="fathersPhone1"
            className="input-field"
            placeholder="Father's Phone 1"
            value={newStudent.fathersPhone1}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="fathersPhone2"
            className="input-field"
            placeholder="Father's Phone 2"
            value={newStudent.fathersPhone2}
            onChange={handleInputChange}
          />
          {/* <input
            type="file"
            name="studentPicture"
            className="input-field"
            onChange={handleInputChange}
          /> */}
          <button type="button" className="login-button caps" onClick={handleAddStudent}>
            Add Student
          </button>
          <button type="button" className="login-button caps" onClick={() => setIsAddModalOpen(false)}>
            Cancel
          </button>
        </form>
      </Modal>

      {/* Edit Student Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          setNewStudent({
            firstName: '',
            middleName: '',
            lastName: '',
            gender: '',
            dateOfBirth: '',
            bloodGroup: '',
            religion: '',
            homeAddress: '',
            city: '',
            nationality: '',
            NIN: '',
            fathersName: '',
            fathersAddress: '',
            fathersPlaceOfWork: '',
            fathersEmail: '',
            fathersPhone1: '',
            fathersPhone2: '',
            studentPicture: null,
          });
          setCurrentStudent(null);
        }}
        contentLabel="Edit Student"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Student</h2>
        <form className="student-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>           
          <input
            type="text"
            name="firstName"
            className="input-field"
            placeholder="First Name"
            value={newStudent.firstName}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="middleName"
            className="input-field"
            placeholder="Middle Name"
            value={newStudent.middleName}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="lastName"
            className="input-field"
            placeholder="Last Name"
            value={newStudent.lastName}
            onChange={handleInputChange}
          />
          <select
            name="status"
            className="input-field"
            value={newStudent.status || 'Regular'}
            onChange={handleInputChange}
          >
            <option value="Regular">Regular</option>
            <option value="Discount">Discount</option>
            <option value="Scholarship">Scholarship</option>
          </select>

          {newStudent.status === 'Discount' && (
            <div className="conditional-field">
              <label className='size5 bold'>Discount Amount (₦)</label>
              <input
                type="number"
                name="discountAmount"
                className="input-field"
                placeholder="Enter discount amount"
                value={newStudent.discountAmount || ''}
                onChange={handleInputChange}
                min="0"
                step="100"
              />
            </div>
          )}
          <select
            name="gender"
            className="input-field"
            value={newStudent.gender}
            onChange={handleInputChange}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
          <input
            type="date"
            name="dateOfBirth"
            className="input-field"
            value={newStudent.dateOfBirth}
            onChange={handleInputChange}
          />
          <select
            name="bloodGroup"
            className="input-field"
            value={newStudent.bloodGroup}
            onChange={handleInputChange}
          >
            <option value="">Select Blood Group</option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="O">O</option>
            <option value="AB">AB</option>
          </select>
          <select
            name="religion"
            className="input-field"
            value={newStudent.religion}
            onChange={handleInputChange}
          >
            <option value="">Select Religion</option>
            <option value="Islam">Islam</option>
            <option value="Christianity">Christianity</option>
            <option value="Not Specified">Not Specified</option>
          </select>
          <input
            type="text"
            name="homeAddress"
            className="input-field"
            placeholder="Home Address"
            value={newStudent.homeAddress}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="city"
            className="input-field"
            placeholder="City"
            value={newStudent.city}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="nationality"
            className="input-field"
            placeholder="Nationality"
            value={newStudent.nationality}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="NIN"
            className="input-field"
            placeholder="NIN"
            value={newStudent.NIN}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="fathersName"
            className="input-field"
            placeholder="Father's Name"
            value={newStudent.fathersName}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="fathersAddress"
            className="input-field"
            placeholder="Father's Address"
            value={newStudent.fathersAddress}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="fathersPlaceOfWork"
            className="input-field"
            placeholder="Place of Work"
            value={newStudent.fathersPlaceOfWork}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="fathersEmail"
            className="input-field"
            placeholder="Father's Email"
            value={newStudent.fathersEmail}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="fathersPhone1"
            className="input-field"
            placeholder="Father's Phone 1"
            value={newStudent.fathersPhone1}
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="fathersPhone2"
            className="input-field"
            placeholder="Father's Phone 2"
            value={newStudent.fathersPhone2}
            onChange={handleInputChange}
          />
          {/* <input
            type="file"
            name="studentPicture"
            className="input-field"
            onChange={handleInputChange}
          /> */}
          <button type="button" className="login-button caps" onClick={handleEditStudent}>
            Save Changes
          </button>
          <button type="button" className="login-button caps" onClick={() => {
                setIsEditModalOpen(false);
                setNewStudent({
                  firstName: '',
                  middleName: '',
                  lastName: '',
                  gender: '',
                  dateOfBirth: '',
                  bloodGroup: '',
                  religion: '',
                  homeAddress: '',
                  city: '',
                  nationality: '',
                  NIN: '',
                  fathersName: '',
                  fathersAddress: '',
                  fathersPlaceOfWork: '',
                  fathersEmail: '',
                  fathersPhone1: '',
                  fathersPhone2: '',
                  studentPicture: null,
                });
                setCurrentStudent(null);
              }}>
            Cancel
          </button>
        </form>
      </Modal>

      {/* Delete Student Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => {
          setIsDeleteModalOpen(false);
          setCurrentStudent(null);
        }}
        contentLabel="Delete Student"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Student</h2>
        {currentStudent && (
          <div>
            <p className="male-female-stat">Are you sure you want to delete <strong>{currentStudent.firstName} {currentStudent.lastName}</strong>?</p>
            <div className="row align-center gap-1">            
              <button className="login-button" type="button" onClick={() => {
                  setIsDeleteModalOpen(false);
                  setCurrentStudent(null);
                }}>Cancel</button>
              <button className="login-button" type="button" onClick={() => handleDeleteStudent(currentStudent.id)}>Delete</button>
            </div>            
          </div>
        )}
      </Modal>

      {/* Bulk Import Modal */}
      <Modal
        isOpen={isBulkImportModalOpen}
        onRequestClose={() => {
          setIsBulkImportModalOpen(false);
          setFile(null);
          setImportProgress(0);
          setImportStatus('');
        }}
        contentLabel="Bulk Import Students"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Bulk Import Students</h2>
        <div className="modal-form">
          <div style={{marginBottom: '5px'}}>
            <label>
              <input
                type="radio"
                name="fileType"
                value="excel"
                checked={fileType === 'excel'}
                onChange={handleFileTypeChange}
              /> Excel File (.xlsx)
            </label>
            <label>
              <input
                type="radio"
                name="fileType"
                value="csv"
                checked={fileType === 'csv'}
                onChange={handleFileTypeChange}
              /> CSV File (.csv)
            </label>
          </div>
          
          <input
            type="file"
            accept={fileType === 'excel' ? '.xlsx, .xls' : '.csv'}
            onChange={handleFileChange}
            className="input-field1"
          />
          
          {importStatus && (
            <div>
              <p>{importStatus}</p>
              {importProgress > 0 && (
                <div style={{ width: '100%', backgroundColor: '#ddd', borderRadius: '5px' }}>
                  <div
                    style={{
                      width: `${importProgress}%`,
                      height: '20px',
                      backgroundColor: '#4CAF50',
                      borderRadius: '5px',
                      textAlign: 'center',
                      lineHeight: '20px',
                      color: 'white'
                    }}
                  >
                    {importProgress}%
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="row align-center justify-center gap-15">
            <button
              type="button"
              className="login-button caps"
              onClick={() => {
                setIsBulkImportModalOpen(false);
                setFile(null);
                setImportProgress(0);
                setImportStatus('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="login-button caps"
              onClick={processBulkImport}
              disabled={!file || update}
            >
              Import
            </button>
          </div>
          
          <div className="import-instructions">
            <h4>File Format Instructions:</h4>
            <p><strong>REQUIRED FIELDS:</strong> firstName, lastName, gender, religion, city, nationality</p>
            <p><strong>OPTIONAL FIELDS:</strong> middleName, dateOfBirth, bloodGroup, homeAddress, city, NIN, fathersName, fathersAddress, fathersPlaceOfWork, fathersEmail, fathersPhone1, fathersPhone2</p>
            <p><strong>FOR EXCEL:</strong> First row should contain column headers</p>
            <p><strong>FOR CSV:</strong> First row should contain column headers, comma-separated</p>
          </div>
        </div>
      </Modal>

      {/* View Student Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onRequestClose={() => setIsViewModalOpen(false)}
        contentLabel="View Student"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>View Student</h2>
        {currentStudent && (
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <p className="male-female-stat"><strong>ID:</strong> {currentStudent.id}</p>
            <p className="male-female-stat"><strong>First Name:</strong> {currentStudent.firstName}</p>
            <p className="male-female-stat"><strong>Middle Name:</strong> {currentStudent.middleName}</p>
            <p className="male-female-stat"><strong>Last Name:</strong> {currentStudent.lastName}</p>
            <p className="male-female-stat"><strong>Status:</strong> {currentStudent.status || 'Regular'}</p>
            {currentStudent.status === 'Discount' && (
              <p className="male-female-stat">
                <strong>Discount Amount:</strong> ₦{Number(currentStudent.discountAmount || 0).toLocaleString()}
              </p>
            )}
            <p className="male-female-stat"><strong>Admission No:</strong> {currentStudent.admissionNumber}</p>
            <p className="male-female-stat"><strong>Gender:</strong> {currentStudent.gender}</p>
            <p className="male-female-stat"><strong>Date of Birth:</strong> {currentStudent.dateOfBirth}</p>
            <p className="male-female-stat"><strong>Blood Group:</strong> {currentStudent.bloodGroup}</p>
            <p className="male-female-stat"><strong>Religion:</strong> {currentStudent.religion}</p>
            <p className="male-female-stat"><strong>Home Address:</strong> {currentStudent.homeAddress}</p>
            <p className="male-female-stat"><strong>City:</strong> {currentStudent.city}</p>
            <p className="male-female-stat"><strong>Nationality:</strong> {currentStudent.nationality}</p>
            <p className="male-female-stat"><strong>NIN:</strong> {currentStudent.NIN}</p>
            <p className="male-female-stat"><strong>Father's Name:</strong> {currentStudent.fathersName}</p>
            <p className="male-female-stat"><strong>Address:</strong> {currentStudent.fathersAddress}</p>
            <p className="male-female-stat"><strong>Place of Work:</strong> {currentStudent.fathersPlaceOfWork}</p>
            <p className="male-female-stat"><strong>Email:</strong> {currentStudent.fathersEmail}</p>
            <p className="male-female-stat"><strong>Phone 1:</strong> {currentStudent.fathersPhone1}</p>
            <p className="male-female-stat"><strong>Phone 2:</strong> {currentStudent.fathersPhone2}</p>
            {/* {currentStudent.studentPicture && (
              <div>
                <p className="male-female-stat"><strong>Student Picture:</strong></p>
                <img className="student-picture" src={currentStudent.studentPicture} alt="Student" />
              </div>
            )} */}
            <button type="button" className="login-button caps" onClick={() => setIsViewModalOpen(false)}>
              Close
            </button>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Students;
